import {ReactNode, useContext, useMemo} from "react";
import {dialogContext, OpenDialog} from "./context";
import {ProductContext} from "../../Products/SharedPages/ProductContext";

export type DialogControlProps = {open: boolean; onClose: () => void; onCloseComplete?: () => void};

// Dialogs are rendered by the root `DialogProvider`, outside the route tree and
// therefore outside any `ProductContext.Provider`. To keep product-aware
// behaviour working inside dialogs (e.g. user pickers that only list users with
// access to the current product), we capture the ambient product at `open()`
// time — where the caller is still inside the product tree — and stash it under
// this key. `DialogProvider` strips it back out and re-provides `ProductContext`
// around the rendered dialog.
export const ambientProductKey = Symbol("ambientProduct");

export function useDialogContext() {
  const {open: rawOpen} = useContext(dialogContext);
  const product = useContext(ProductContext);

  return useMemo<{open: OpenDialog}>(
    () => ({
      open: (dialogId, ...args) => {
        const dialogProps = (args[0] ?? {}) as Record<PropertyKey, unknown>;
        rawOpen(dialogId, {...dialogProps, [ambientProductKey]: product} as never);
      },
    }),
    [rawOpen, product],
  );
}

const dialogProps = Symbol("dialogProps");
export type DialogId<T> = symbol & {[dialogProps]: T};

export type DialogRenderFunc<T = any> = (props: DialogControlProps & T) => ReactNode;
export type ProvidedDialog<T = any> = [DialogId<T>, DialogRenderFunc];

export function declareDialog<T = Record<PropertyKey, never>>(): DialogId<T> {
  return Symbol() as DialogId<T>;
}

export function provideDialog<T>(dialogId: DialogId<T>, f: DialogRenderFunc<T>): ProvidedDialog {
  return [dialogId, f];
}
